import type { Money, PropertyState } from "./types.js";

/**
 * How far a parcel reaches, and what it costs to reach further.
 *
 * Everyone starts on the smallest yard. That is deliberate: the largest items — the picnic
 * blanket, the wide garden pieces — have nowhere legal to stand on a tier 0 plot once the cottage
 * is in the middle of it, so wanting to put one down is what makes the expansion worth buying.
 *
 * The ceiling is not arbitrary. Plot centres are `MIN_PLOT_DISTANCE` apart, so the largest radius
 * has to stay under half of that or neighbouring yards would overlap.
 */

/** Plot half-width in voxels, one per tier. */
export const PLOT_RADII = [14, 17, 20] as const;

/** Half-width of a parcel that has never been expanded — where everyone starts. */
export const PLOT_RADIUS: number = PLOT_RADII[0];

/** What the *next* tier costs, indexed by the tier you are buying into. Tier 0 is where you start. */
export const PLOT_EXPANSION_PRICES = [0, 1_500, 4_000] as const;

export const PLOT_MAX_TIER = PLOT_RADII.length - 1;
export const PLOT_RADIUS_MAX: number = PLOT_RADII[PLOT_RADII.length - 1] ?? PLOT_RADII[0];

export function plotRadiusForTier(tier: number): number {
  return PLOT_RADII[Math.min(Math.max(tier, 0), PLOT_MAX_TIER)] ?? PLOT_RADII[0];
}

export function plotRadius(property: Pick<PropertyState, "plotTier">): number {
  return plotRadiusForTier(property.plotTier);
}

export type PlotExpansionResult =
  | { ok: true; nextTier: number; costCopper: number; radius: number }
  | { ok: false; message: string };

/**
 * The single rule for expanding a parcel, shared by the activity and the worker so an optimistic
 * update can never disagree with what the server will allow. Mirrors `evaluateHomeChoice`.
 */
export function evaluatePlotExpansion(input: {
  property: Pick<PropertyState, "plotTier">;
  wallet: Money;
}): PlotExpansionResult {
  const nextTier = input.property.plotTier + 1;
  if (nextTier > PLOT_MAX_TIER) {
    return { ok: false, message: "Your parcel already reaches as far as it can" };
  }
  const costCopper = PLOT_EXPANSION_PRICES[nextTier] ?? 0;
  if (input.wallet.copper < costCopper) {
    return { ok: false, message: "You do not have enough coins to expand your parcel yet" };
  }
  return { ok: true, nextTier, costCopper, radius: plotRadiusForTier(nextTier) };
}
